/* eslint-disable no-param-reassign */
import React, { useContext, useState, useEffect } from 'react';
import proptypes from 'prop-types';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import {
  arrayOf as arrayOfType,
  bool as boolType,
  number as numberType,
  shape as shapeType,
  string as stringType,
  useDataModel, params, client
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import { Radio } from '@one-thd/sui-atomic-components';
import { FulfillmentPodPlaceholder } from './FulfillmentPodPlaceholder';
import { FulfillmentPodContext } from '../FulfillmentPod';
import { getStoreTemplate } from '../helpers/store-helper';
import { resolveSelectedFulfillmentMethod } from '../helpers/select-fulfillment';
import {
  isBrioProduct,
  isCustomKitchenCabinetProduct,
  isPodStoreEqual
} from '../helpers/pod-fulfillment-utils';
import { EmptyStoreMessage } from '../partials/store/EmptyStoreMessage';
import { BundleFulfillmentMessage } from '../partials/store/BundleFulfillmentMessage';
import { renderPickupTemplates } from '../templates/pickUpTemplate';
import { CHECK_NEARBY_STORE } from '../../components/constants';

const { bool, string, object, func } = proptypes;

export const FulfillmentPodStore = React.memo(({
  itemId = '',
  storeId: primaryStoreId = undefined,
  zipCode = undefined,
  useCustomTemplate = false,
  hideForChooseYourOptions = false,
  directData = null,
  isNarrowPod = false,
  isSelectable = false,
  selectedFulfillmentMethod = null,
  onFulfillmentMethodSelect = null,
  bulkPreference = null
}) => {
  const podContext = useContext(FulfillmentPodContext) || {};
  const { storeId: secondaryStoreId } = useStore();
  const storeId = primaryStoreId || secondaryStoreId;
  const { customer } = useContext(ExperienceContext);
  const [product, setProduct] = useState(null);
  const isHideLimitedStockClearanceEnabled = !!useConfigService('fs:isHideLimitedStockClearanceEnabled');
  const isNewFulfillmentPodMessagingEnabled = !!useConfigService('fs:enableNewFulfillmentPodMessaging');
  const { additionalData = {} } = useImpression({
    data: {
      id: itemId,
      component: 'ProductStore',
      name: 'ProductStore',
      position: 0
    }
  });
  const opts = {
    variables: {
      itemId,
      storeId,
      zipCode
    },
    skip: !itemId || !!directData,
    ssr: false
  };
  const { data, loading, error } = useDataModel('product', opts);

  const productData = data?.product || directData;

  useEffect(() => {
    if (productData) {
      setProduct(productData);
    }
  }, [productData]);

  if (loading || (
    typeof (data) === 'undefined' && !error && !product
  )) return <FulfillmentPodPlaceholder />;
  if (!product) { return EmptyStoreMessage; }

  const bundleFlag = !!product?.bundleFlag;
  const fulfillmentBundleMessage = product?.fulfillment?.fulfillmentBundleMessage;

  const nullConditions = [
    hideForChooseYourOptions && product?.info?.isGenericProduct,
    product?.info?.productSubType?.name === 'HDQC',
    !!product?.installServices?.scheduleAMeasure,
    isCustomKitchenCabinetProduct(product),
    isBrioProduct(product)
  ];

  if (nullConditions.some((nullCondition) => nullCondition)) {
    return null;
  }

  const { parent } = additionalData || {};

  const handleClick = () => {
    LIFE_CYCLE_EVENT_BUS.trigger('product-pod-v7.click', {
      podAction: CHECK_NEARBY_STORE,
      podAnchorSku: itemId,
      parent,
      target: ''
    });
  };

  const StoreComponent = getStoreTemplate(
    {
      product,
      storeId,
      customer,
      isHideLimitedStockClearanceEnabled,
      customRender: useCustomTemplate ? renderPickupTemplates : null,
      isNewFulfillmentPodMessagingEnabled,
      isNarrowPod
    });

  if (!StoreComponent) { return EmptyStoreMessage; }

  if (bundleFlag) {
    return null;
  }

  // The fulfillment method this outcome implies (null for disabled/empty outcomes).
  const fulfillmentMethod = StoreComponent?.fulfillmentMethod ?? null;
  const showSelector = isSelectable && !!fulfillmentMethod;
  // Only resolve when a radio is actually shown, so pods for consumers that don't opt into
  // selection never touch the shared resolver and behave exactly as before.
  const isChecked = showSelector && resolveSelectedFulfillmentMethod(product, {
    selectedMethod: selectedFulfillmentMethod,
    bulkPreference,
    storeId,
    isHideLimitedStockClearanceEnabled
  }) === fulfillmentMethod;

  const content = (
    <span
      className={showSelector ? 'sui-leading-tight sui-flex sui-items-start sui-gap-1' : 'sui-leading-tight'}
      data-component="FulfillmentPodStore"
    >
      {showSelector && (
        // Negative margins pull the radio bubble flush-left to line up with the icon column.
        // The sui Radio nests two layers of left padding — the root span's sui-pl-1 (4px) plus
        // the icon span's sui-p-1 (4px) — so -sui-ml-2 (-8px) is needed to cancel both; -sui-mr-1
        // trims the outer right padding. Neither is overridable via a className on the Radio.
        <span className="sui-flex -sui-ml-2 -sui-mr-1">
          <Radio
            value={fulfillmentMethod}
            name={`fulfillment-method-${itemId}`}
            checked={isChecked}
            onChange={() => { if (onFulfillmentMethodSelect) onFulfillmentMethodSelect(fulfillmentMethod); }}
            inputAttributes={{ 'aria-label': `Select store fulfillment: ${fulfillmentMethod}` }}
          />
        </span>
      )}
      <StoreComponent
        product={product}
        onCheckNearbyStoresClick={handleClick}
      />
    </span>
  );

  // When a radio is shown it takes the place of the pickup icon, so hide the icon (via context)
  // for that outcome only. Disabled/unavailable outcomes render no radio and keep their icon —
  // including the "unavailable" (X) icon. Consumers that don't opt into selection are untouched.
  return showSelector
    ? (
      <FulfillmentPodContext.Provider value={{ ...podContext, hideIcon: true }}>
        {content}
      </FulfillmentPodContext.Provider>
    )
    : content;
}, isPodStoreEqual);

FulfillmentPodStore.propTypes = {
  itemId: string.isRequired,
  storeId: string,
  zipCode: string,
  useCustomTemplate: bool,
  hideForChooseYourOptions: bool,
  // eslint-disable-next-line react/forbid-prop-types
  directData: object,
  isNarrowPod: bool,
  isSelectable: bool,
  selectedFulfillmentMethod: string,
  onFulfillmentMethodSelect: func,
  bulkPreference: string
};

const installServices = params({ storeId: stringType(), zipCode: stringType() }).shape({
  scheduleAMeasure: boolType()
});
if (installServices.skip) {
  installServices.skip('skipInstallServices', true);
}

const Service = shapeType({
  hasFreeShipping: boolType(),
  freeDeliveryThreshold: numberType({ float: true }),
  earliestDeliveryDate: stringType(),
  locations: arrayOfType(shapeType({
    curbsidePickupFlag: boolType(),
    isBuyInStoreCheckNearBy: boolType(),
    distance: numberType({ float: true }),
    inventory: shapeType({
      isOutOfStock: boolType(),
      isInStock: boolType(),
      isLimitedQuantity: boolType(),
      isUnavailable: boolType(),
      quantity: numberType(),
      maxAllowedBopisQty: numberType(),
      minAllowedBopisQty: numberType()
    }),
    isAnchor: boolType(),
    locationId: stringType(),
    state: stringType(),
    storeName: stringType(),
    storePhone: stringType(),
    type: stringType()
  })),
  type: stringType(),
  totalCharge: numberType({ float: true })
});

FulfillmentPodStore.dataModel = {
  product: params({
    itemId: stringType().isRequired(),
    dataSource: stringType()
  })
    .shape({
      itemId: stringType(),
      bundleFlag: boolType(),
      info: shapeType({
        globalCustomConfigurator: shapeType({
          customExperience: stringType()
        }),
        productSubType: shapeType({
          name: stringType()
        }),
        isBrioSku: boolType(),
        isCustomKitchenCabinet: boolType(),
        isGenericProduct: boolType()
      }),
      dataSources: stringType(),
      availabilityType: shapeType({
        buyable: boolType(),
        discontinued: boolType(),
        status: boolType(),
        type: stringType()
      }),
      fulfillment: client(params({ storeId: stringType(), zipCode: stringType() }).shape({
        anchorStoreStatus: boolType(),
        anchorStoreStatusType: stringType(),
        backordered: boolType(),
        backorderedShipDate: stringType(),
        bossExcludedShipStates: stringType(),
        excludedShipStates: stringType(),
        seasonStatusEligible: boolType(),
        fulfillmentOptions: arrayOfType(shapeType({
          type: stringType(),
          fulfillable: boolType(),
          services: arrayOfType(Service)
        })),
        fulfillmentBundleMessage: stringType()
      })),
      installServices
    })
};

FulfillmentPodStore.displayName = 'FulfillmentPodStore';
